<template>
  <Teleport to="body">
    <transition name="fade">
      <div v-if="isOpen" class="fixed inset-0 z-[9999] select-auto">
      <!-- Overlay -->
      <div
        class="absolute inset-0 bg-black opacity-70"
        @click.stop="close"
      />

      <!-- iOS non-Safari: Show "Open in Safari" instructions -->
      <div
        v-if="isNonSafariiOS"
        class="absolute inset-0 flex items-center justify-center"
      >
        <div class="w-screen mx-2 xl:min-w-[400px] max-w-md p-6 relative xl:mx-auto my-auto shadow-lg bg-white z-10 text-center">
          <button
            class="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            @click.stop="close"
            aria-label="Close"
          >
            <i class="fa fa-times text-lg" />
          </button>

          <div style="width:64px;height:64px;border-radius:16px;background:#f0f7ff;display:flex;align-items:center;justify-content:center;margin:0 auto 16px;">
            <i class="fa fa-compass" style="font-size:28px;color:#007AFF;" />
          </div>
          <h2 class="text-xl font-bold text-black mb-2">Open in Safari for AR</h2>
          <p class="text-gray-500 text-sm mb-4">Augmented Reality requires Safari on iOS. Copy the link and open it in Safari to view your design on your wall.</p>
          <button
            class="inline-flex items-center gap-2 px-6 py-3 text-white font-semibold rounded-xl"
            style="background:linear-gradient(135deg,#5AC8FA,#007AFF);"
            @click="copyLink"
          >
            <i :class="linkCopied ? 'fa fa-check' : 'fa fa-copy'" />
            {{ linkCopied ? 'Copied!' : 'Copy Link' }}
          </button>
          <p v-if="props.arPageUrl" class="text-xs text-gray-400 mt-3 break-all">{{ props.arPageUrl }}</p>
        </div>
      </div>

      <!-- Desktop: QR code modal (matching app style) -->
      <div
        v-else-if="!arSupported"
        class="absolute inset-0 flex items-center justify-center"
      >
        <div class="w-screen mx-2 xl:min-w-[400px] max-w-md p-6 relative xl:mx-auto my-auto shadow-lg bg-white z-10">
          <!-- Close button -->
          <button
            class="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            @click.stop="close"
            aria-label="Close"
          >
            <i class="fa fa-times text-lg" />
          </button>

          <!-- Loading state -->
          <div v-if="loading" class="text-center py-8">
            <i class="fa fa-spinner fa-spin fa-2x text-gray-600" />
            <p class="mt-4 text-gray-600">Preparing 3D model...</p>
          </div>

          <!-- Error state -->
          <div v-else-if="error" class="text-center py-8">
            <i class="fa fa-exclamation-triangle fa-2x text-red-500" />
            <p class="mt-4 text-gray-800">{{ error }}</p>
            <button
              class="mt-4 bg-black px-5 py-2 text-white hover:bg-gray-800 rounded-full"
              @click="$emit('retry')"
            >
              Try Again
            </button>
          </div>

          <!-- QR Code content -->
          <div v-else-if="props.arPageUrl" class="text-center">
            <h2 class="text-2xl font-bold text-black mb-4">View in Your Room</h2>
            <div class="flex justify-center mb-4">
              <div class="p-3 bg-gray-50 rounded-lg inline-block">
                <QRCodeVue
                  :value="props.arPageUrl"
                  :size="200"
                  level="M"
                  :margin="2"
                />
              </div>
            </div>
            <p class="text-black font-semibold mb-2">
              <i class="fa fa-mobile-alt mr-2" />
              Scan with your phone
            </p>
            <p class="text-gray-500 text-sm">
              Point your phone camera at this QR code to view your design in augmented reality
            </p>
          </div>
        </div>
      </div>

      <!-- Mobile: Full-screen AR viewer -->
      <div
        v-else
        class="ar-viewer-modal"
        :class="{ 'minimal-mode': props.autoLaunchAR }"
      >
        <div class="ar-viewer-container">
          <!-- Hide close button in minimal/auto-launch mode -->
          <button v-if="!props.autoLaunchAR" class="close-button" @click.stop="close" aria-label="Close">
            <i class="fa fa-times" />
          </button>

          <!-- Loading state - shown in both modes -->
          <div v-if="loading || (props.autoLaunchAR && !modelLoaded)" class="loading-state">
            <i class="fa fa-spinner fa-spin fa-3x" />
            <p>{{ props.autoLaunchAR ? 'Preparing AR experience...' : 'Preparing 3D model...' }}</p>
          </div>

          <div v-else-if="error" class="error-state">
            <i class="fa fa-exclamation-triangle fa-3x" />
            <p>{{ error }}</p>
            <button class="retry-button" @click="$emit('retry')">
              Try Again
            </button>
          </div>

          <!-- iOS non-Safari: Show "Open in Safari" instructions -->
          <div v-if="isNonSafariiOS && !loading && !error" class="open-safari-state">
            <div class="safari-icon">
              <i class="fa fa-compass fa-3x" />
            </div>
            <h2>Open in Safari for AR</h2>
            <p>Augmented Reality requires Safari on iOS. Copy the link below and open it in Safari to view your design on your wall.</p>
            <button class="copy-link-button" @click="copyLink">
              <i :class="linkCopied ? 'fa fa-check' : 'fa fa-copy'" />
              {{ linkCopied ? 'Copied!' : 'Copy Link' }}
            </button>
            <p v-if="props.arPageUrl" class="link-text">{{ props.arPageUrl }}</p>
          </div>

          <!-- Show loading while model-viewer is loading (non-minimal mode only) -->
          <div v-if="!isNonSafariiOS && !props.autoLaunchAR && modelUrl && !loading && !error && !modelViewerLoaded" class="loading-state">
            <i class="fa fa-spinner fa-spin fa-3x" />
            <p>Loading 3D viewer...</p>
          </div>

          <!-- Mobile: model-viewer with AR button -->
          <template v-if="!isNonSafariiOS && modelUrl && !loading && !error && modelViewerLoaded">
            <model-viewer
              :src="modelUrl"
              ar
              ar-modes="webxr scene-viewer quick-look"
              ar-placement="wall"
              ar-scale="fixed"
              camera-controls
              touch-action="pan-y"
              :auto-rotate="!modelLoaded"
              shadow-intensity="1"
              environment-image="neutral"
              class="model-viewer"
              :class="{ 'model-viewer-hidden': props.autoLaunchAR }"
              @load="onModelLoad"
            >
              <button slot="ar-button" class="ar-button">
                <i class="fa fa-cube" />
                View in Your Room
              </button>

              <div class="poster" slot="poster">
                <i class="fa fa-spinner fa-spin fa-2x" />
                <p>Loading 3D model...</p>
              </div>
            </model-viewer>

            <!-- Instructions for mobile (non-minimal mode only) -->
            <div v-if="!props.autoLaunchAR" class="instructions">
              <p v-if="isIOS">Tap the green button to view in AR using Quick Look</p>
              <p v-else>Tap the green "View in Your Room" button to place this design on your wall</p>
            </div>
          </template>
        </div>
      </div>
    </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import QRCodeVue from 'qrcode.vue'

const modelViewerLoaded = ref(false)
const modelLoaded = ref(false)
const arSupported = ref(false)
const isIOS = ref(false)
const isNonSafariiOS = ref(false)
const linkCopied = ref(false)

const props = defineProps<{
  isOpen: boolean
  modelUrl: string | null
  iosSrc?: string | null
  loading: boolean
  error: string | null
  autoLaunchAR?: boolean  // Auto-launch AR when model loads (for direct mobile flow)
  arPageUrl?: string | null  // URL for the AR page (used for QR code on desktop)
}>()

const emit = defineEmits<{
  close: []
  retry: []
}>()

const close = () => {
  emit('close')
}

// Handle model-viewer load event - stops auto-rotate to prevent warning spam (BUG-003)
const onModelLoad = () => {
  modelLoaded.value = true
  // Check if AR is supported after model loads
  checkArSupport()

  // Auto-launch AR on mobile if requested (for direct mobile flow)
  if (props.autoLaunchAR && arSupported.value) {
    // Delay to ensure model-viewer is fully ready for AR
    setTimeout(() => {
      const modelViewer = document.querySelector('model-viewer') as any
      if (modelViewer?.activateAR) {
        modelViewer.activateAR()
        // Close the modal after a short delay - Quick Look will take over the screen
        setTimeout(() => {
          emit('close')
        }, 300)
      }
    }, 500)
  }
}

// Copy AR page URL to clipboard
const copyLink = async () => {
  const url = props.arPageUrl
  if (!url) return
  try {
    await navigator.clipboard.writeText(url)
    linkCopied.value = true
    setTimeout(() => { linkCopied.value = false }, 2000)
  } catch {
    // Fallback for older browsers
    const input = document.createElement('input')
    input.value = url
    document.body.appendChild(input)
    input.select()
    document.execCommand('copy')
    document.body.removeChild(input)
    linkCopied.value = true
    setTimeout(() => { linkCopied.value = false }, 2000)
  }
}

// Check if AR is supported on this device
const checkArSupport = () => {
  const ua = navigator.userAgent
  isIOS.value = /iPhone|iPad|iPod/.test(ua)
  const isAndroid = /Android/.test(ua)

  // Feature-detect Quick Look support: only real Safari supports rel="ar" on anchor elements
  const a = document.createElement('a')
  const supportsQuickLook = a.relList && a.relList.supports && a.relList.supports('ar')
  isNonSafariiOS.value = isIOS.value && !supportsQuickLook

  // AR supported = (iOS + Quick Look) or Android
  arSupported.value = (isIOS.value && supportsQuickLook) || isAndroid
}

// Reset model loaded state when modal closes or URL changes
watch(() => props.isOpen, (isOpen) => {
  if (!isOpen) {
    modelLoaded.value = false
  }
})

watch(() => props.modelUrl, () => {
  modelLoaded.value = false
})

// Handle escape key
const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && props.isOpen) {
    close()
  }
}

onMounted(async () => {
  window.addEventListener('keydown', handleKeydown)

  // Check AR support on mount
  checkArSupport()

  // Dynamically import model-viewer only on client side
  try {
    await import('@google/model-viewer')
    modelViewerLoaded.value = true
  } catch (e) {
    console.error('Failed to load model-viewer:', e)
  }
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
/* Fade transition */
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 300ms ease-out;
}

/* Mobile AR viewer (full-screen) */
.ar-viewer-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ar-viewer-container {
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 100vw;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* BUG-006 fix: Improved close button with larger touch target */
.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 100;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  font-size: 22px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.close-button:hover,
.close-button:active {
  background: white;
  transform: scale(1.05);
}

.close-button:focus {
  outline: 2px solid #4CAF50;
  outline-offset: 2px;
}

.model-viewer {
  width: 100%;
  flex: 1;
  min-height: 0;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
}

/* Hidden model-viewer for minimal/auto-launch mode - still loads but not visible */
.model-viewer-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

/* Minimal mode - just show loading overlay */
.minimal-mode {
  background: rgba(0, 0, 0, 0.85);
}

.minimal-mode .ar-viewer-container {
  justify-content: center;
}

.minimal-mode .loading-state {
  flex: none;
}

/* BUG-008 fix: More prominent AR button */
.ar-button {
  background: linear-gradient(135deg, #4CAF50 0%, #43A047 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 16px 32px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.5);
  transition: all 0.2s ease;
  animation: pulse-ar 2s infinite;
}

@keyframes pulse-ar {
  0%, 100% {
    box-shadow: 0 6px 20px rgba(76, 175, 80, 0.5);
  }
  50% {
    box-shadow: 0 6px 30px rgba(76, 175, 80, 0.8);
  }
}

.ar-button:hover {
  background: linear-gradient(135deg, #43A047 0%, #388E3C 100%);
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(76, 175, 80, 0.6);
}

.ar-button i {
  font-size: 20px;
}

.poster {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: white;
  gap: 16px;
}

.loading-state,
.error-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  gap: 16px;
  text-align: center;
  padding: 24px;
}

.error-state i {
  color: #ff6b6b;
}

.retry-button {
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  cursor: pointer;
  margin-top: 8px;
}

.retry-button:hover {
  background: #43A047;
}

.instructions {
  padding: 16px;
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  background: rgba(0, 0, 0, 0.5);
}

.instructions p {
  margin: 0;
}

/* Open in Safari instructions */
.open-safari-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  gap: 16px;
  text-align: center;
  padding: 32px 24px;
}

.safari-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.safari-icon i {
  color: #5AC8FA;
}

.open-safari-state h2 {
  font-size: 22px;
  font-weight: 700;
  margin: 0;
}

.open-safari-state > p {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.7);
  max-width: 320px;
  line-height: 1.5;
  margin: 0;
}

.copy-link-button {
  background: linear-gradient(135deg, #5AC8FA 0%, #007AFF 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px 28px;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  transition: transform 0.15s ease;
}

.copy-link-button:active {
  transform: scale(0.96);
}

.link-text {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  word-break: break-all;
  max-width: 300px;
}
</style>
